// ActionsMenu — a table row's ⋯ button and its popover of actions.
//
// Same look and behaviour as the Users table's row menu: portalled so the
// table's overflow can't clip it, anchored under the trigger's right edge,
// flipped upward near the bottom of the viewport, closed by an outside
// click or Escape.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type ActionsMenuItem = {
  label: string;
  onSelect: () => void;
  tone?: 'danger';
  /** Draw a divider above this item. */
  separated?: boolean;
};

const WIDTH = 200;
const ITEM_H = 38;

export function ActionsMenu({
  items,
  disabled,
  label = 'Row actions',
}: {
  items: ActionsMenuItem[];
  disabled?: boolean;
  label?: string;
}) {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);

  return (
    <>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        disabled={disabled}
        onClick={(e) => setAnchor(anchor ? null : e.currentTarget)}
        style={{
          background: anchor ? 'var(--bg)' : 'transparent',
          border: 'none',
          borderRadius: 8,
          padding: '4px 10px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: 'var(--ink-2)',
          fontSize: 18,
          lineHeight: 1,
          fontFamily: 'var(--font-ui)',
        }}
      >
        ⋯
      </button>
      {anchor && (
        <MenuPopover
          anchor={anchor}
          items={items}
          onClose={() => setAnchor(null)}
        />
      )}
    </>
  );
}

function MenuPopover({
  anchor,
  items,
  onClose,
}: {
  anchor: HTMLElement;
  items: ActionsMenuItem[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const update = () => {
      const r = anchor.getBoundingClientRect();
      const height = 12 + items.length * ITEM_H + items.filter((i) => i.separated).length * 9;
      let top = r.bottom + 6;
      if (top + height > window.innerHeight - 8) top = Math.max(8, r.top - height - 6);
      let left = r.right - WIDTH;
      if (left < 8) left = 8;
      if (left + WIDTH > window.innerWidth - 8) left = window.innerWidth - 8 - WIDTH;
      setPos({ top, left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchor, items]);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t || ref.current?.contains(t) || anchor.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        anchor.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  // Move focus into the menu so keyboard users land on the first action.
  useEffect(() => {
    if (pos) ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [pos]);

  if (!pos) return null;

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: WIDTH,
        background: 'var(--surface)',
        border: '1px solid var(--line-strong)',
        borderRadius: 12,
        boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
        padding: 6,
        zIndex: 1000,
      }}
    >
      {items.map((item) => (
        <div key={item.label}>
          {item.separated && (
            <div style={{ borderTop: '1px solid var(--line)', margin: '4px 0' }} />
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onClose();
              item.onSelect();
            }}
            style={{
              display: 'block',
              width: '100%',
              padding: '9px 12px',
              background: 'transparent',
              border: 'none',
              borderRadius: 8,
              textAlign: 'left',
              cursor: 'pointer',
              color: item.tone === 'danger' ? '#a8331f' : 'var(--ink)',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'var(--font-ui)',
            }}
          >
            {item.label}
          </button>
        </div>
      ))}
    </div>,
    document.body,
  );
}
